import React, { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { observed, methodologyItems, reading } from "../../data/reportData";
import { ArrowUpRight } from "lucide-react";
import { CircledWord } from "../CircledWord";
import { useIsMobile } from "../../hooks/use-mobile";

const GREEN = "#53B851";

const fadeUp = {
  initial: { opacity: 0, y: 36 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
};

function SourcesLine() {
  const key = "300 structured operator interviews.";
  const idx = observed.sources.indexOf(key);
  if (idx === -1) return observed.sources;
  return (
    <>
      {observed.sources.slice(0, idx)}
      <span className="font-semibold" style={{ color: GREEN }}>{key}</span>
      {observed.sources.slice(idx + key.length)}
    </>
  );
}

// ── Methodology: all items visible, words fill from 20% → 100% opacity on scroll ──
function FillItem({ m, i }) {
  const ref = useRef(null);
  const wordsRef = useRef([]);
  const labelRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.85", "end 0.5"] });

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    const N = wordsRef.current.length;
    wordsRef.current.forEach((el, wi) => {
      if (!el) return;
      const t = Math.max(0, Math.min(1, v * N - wi));
      el.style.opacity = (0.2 + 0.8 * t).toFixed(3);
    });
    if (labelRef.current) labelRef.current.style.opacity = v > 0.02 ? 1 : 0.35;
  });

  return (
    <div
      ref={ref}
      className="grid md:grid-cols-[220px_1fr] gap-3 md:gap-10 py-7 md:py-9 border-t border-white/15 last:border-b"
      data-testid={`methodology-item-${i}`}
    >
      <p
        ref={labelRef}
        className="text-[12.5px] tracking-[0.16em] pt-1 transition-opacity duration-500"
        style={{ color: GREEN, opacity: 0.35 }}
      >
        {m.label.toUpperCase()}
      </p>
      <p className="text-[14.5px] md:text-[15px] leading-[1.8] text-[#C9C9C9]">
        {m.body.split(" ").map((w, wi) => (
          <span key={wi} ref={(el) => { wordsRef.current[wi] = el; }} style={{ opacity: 0.2 }}>
            {w}{" "}
          </span>
        ))}
      </p>
    </div>
  );
}

function MethodologySteps() {
  return (
    <div className="mt-12" data-testid="methodology-list">
      {methodologyItems.map((m, i) => (
        <FillItem key={m.label} m={m} i={i} />
      ))}
    </div>
  );
}

// ── Further reading: cards slide in and settle as a visible staggered stack ──
const STACK_OFFSET = 84;

function ReadingCard({ r, i, n, progress }) {
  const start = (i - 1) / (n - 1);
  const end = i / (n - 1);
  const y = useTransform(progress, [start, end], [i * STACK_OFFSET + 480, i * STACK_OFFSET]);
  const inner = (
    <>
      <p className="font-display font-medium text-[20px] md:text-[26px] leading-[1.25] tracking-[-0.01em] text-white max-w-[560px]">
        {r.title}
      </p>
      <div className="flex items-center justify-between mt-10 md:mt-16">
        <span className="text-[13.5px] md:text-[15px]" style={{ color: GREEN }}>{r.host}</span>
        <ArrowUpRight
          size={20}
          className="text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300"
        />
      </div>
    </>
  );
  if (i === 0) {
    return (
      <a
        href={r.url}
        target="_blank"
        rel="noopener noreferrer"
        className="group col-start-1 row-start-1 relative z-[1] block bg-[#0C0D0C] border border-white/15 px-7 md:px-11 pt-9 md:pt-12 pb-7 md:pb-8"
        data-testid={`reading-card-${i}`}
      >
        {inner}
      </a>
    );
  }
  return (
    <motion.a
      href={r.url}
      target="_blank"
      rel="noopener noreferrer"
      style={{ y, zIndex: i + 1 }}
      className="group col-start-1 row-start-1 relative flex flex-col justify-between bg-[#0C0D0C] border border-white/15 px-7 md:px-11 pt-9 md:pt-12 pb-7 md:pb-8"
      data-testid={`reading-card-${i}`}
    >
      {inner}
    </motion.a>
  );
}

function ReadingStack() {
  const ref = useRef(null);
  const isMobile = useIsMobile();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start center", "end end"] });
  const n = reading.length;

  if (isMobile) {
    return (
      <div className="mt-14 space-y-6" data-testid="reading-stack">
        {reading.map((r, i) => (
          <a
            key={i}
            href={r.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group block bg-[#0C0D0C] border border-white/15 px-6 pt-8 pb-6"
            data-testid={`reading-card-${i}`}
          >
            <p className="font-display font-medium text-[20px] leading-[1.3] tracking-[-0.01em] text-white">
              {r.title}
            </p>
            <div className="flex items-center justify-between mt-8">
              <span className="text-[13.5px]" style={{ color: GREEN }}>{r.host}</span>
              <ArrowUpRight size={20} className="text-white" />
            </div>
          </a>
        ))}
      </div>
    );
  }

  return (
    <div ref={ref} className="mt-14 md:mt-20" data-testid="reading-stack">
      <div className="sticky top-[16vh] md:top-[18vh] overflow-hidden grid" style={{ paddingBottom: (n - 1) * STACK_OFFSET }}>
        {reading.map((r, i) => (
          <ReadingCard key={i} r={r} i={i} n={n} progress={scrollYProgress} />
        ))}
      </div>
      <div style={{ height: `${(n - 1) * 60}vh` }} />
    </div>
  );
}

export default function FinalSections() {
  const [titlePre] = observed.title.split("observed");
  return (
    <>
      {/* ── What this index observed ── */}
      <section id="observed" data-testid="section-observed">
        <div className="bg-black py-20 md:py-24">
          <div className="report-col">
            <motion.p
              {...fadeUp}
              className="uppercase text-[12.5px] tracking-[0.2em] font-medium mb-5"
              style={{ color: GREEN }}
            >
              Closing
            </motion.p>
            <motion.h2 {...fadeUp} className="section-h2" style={{ color: "#FFFFFF" }}>
              {titlePre}
              <CircledWord color={GREEN}>observed</CircledWord>
            </motion.h2>
            <motion.div
              {...fadeUp}
              className="mt-12 md:mt-14 pl-6 md:pl-11 py-1"
              style={{ borderLeft: `2px solid ${GREEN}` }}
              data-testid="observed-statement"
            >
              <p className="text-[15.5px] md:text-[17px] leading-[1.85] text-white max-w-[480px]">
                {observed.body.split("one in three").map((part, idx, arr) => (
                  <React.Fragment key={idx}>
                    {part}
                    {idx < arr.length - 1 && <span className="font-semibold" style={{ color: GREEN }}>one in three</span>}
                  </React.Fragment>
                ))}
              </p>
            </motion.div>
            <motion.p {...fadeUp} className="text-[15px] md:text-[16px] text-[#9C9C9C] mt-12 md:mt-14 leading-[1.75] max-w-3xl">
              <SourcesLine />
            </motion.p>
          </div>
        </div>
      </section>

      {/* ── Methodology appendix ── */}
      <section id="methodology" data-testid="section-methodology">
        <div className="bg-black py-20 md:py-24">
          <div className="report-col">
            <motion.p {...fadeUp} className="uppercase text-[12.5px] tracking-[0.2em] font-medium mb-5 text-white/75">
              Appendix
            </motion.p>
            <motion.h2 {...fadeUp} className="section-h2" style={{ color: "#FFFFFF" }}>
              Methodology
            </motion.h2>
            <motion.p {...fadeUp} className="body-text mt-8" style={{ color: "#E6E6E6" }}>
              How every figure in this report is measured, and on what base.
            </motion.p>

            <MethodologySteps />
          </div>
        </div>
      </section>

      {/* ── Further reading: scroll-stack cards ── */}
      <section data-testid="section-reading">
        <div className="bg-black py-20 md:py-24">
          <div className="report-col">
            <motion.p
              {...fadeUp}
              className="uppercase text-[12.5px] tracking-[0.2em] font-medium mb-5"
              style={{ color: GREEN }}
            >
              Further reading
            </motion.p>
            <motion.h2 {...fadeUp} className="section-h2" style={{ color: "#FFFFFF" }}>
              Relevant posts &amp; resources
            </motion.h2>

            <ReadingStack />
          </div>
        </div>
      </section>
    </>
  );
}
